// "What can they see?" — plain summary of a person's access and what differs from their role preset.
import { Eye } from "lucide-react";
import { ACCESS_ROLE_LABELS } from "../../lib/access";
import { useCapabilityCatalog } from "./AbilitiesEditor";

const chip = (cls) => `inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-mono ${cls}`;
const NAMES = { none: "No access", view: "View", edit: "Edit", manage: "Manage" };

export default function AccessSummary({ detail, groups }) {
  const caps = useCapabilityCatalog();
  const label = Object.fromEntries(groups.flatMap((g) => g.children.map((c) => [c.page_id, `${g.label} › ${c.label}`])));
  const isAdmin = detail.role === "admin" || detail.super_admin;
  const role = detail.access_role;
  const level = detail.super_admin ? "Super admin" : isAdmin ? "Admin" : role ? ACCESS_ROLE_LABELS[role] : "Unrestricted";
  const eff = detail.effective_levels;
  const base = role === "view_only" ? {} : detail.preset_levels || {};
  const counts = eff ? Object.values(eff).reduce((a, l) => ({ ...a, [l]: (a[l] || 0) + 1 }), {}) : null;
  const diffs = eff && !isAdmin ? [...new Set([...Object.keys(eff), ...Object.keys(base)])].filter((id) => (eff[id] || "none") !== (base[id] || "none")) : [];
  const capOv = Object.fromEntries((detail.capability_overrides || []).map((o) => [o.key, o.granted]));
  const has = new Set(detail.capabilities || []);
  return (
    <div className="rounded-xl border border-sky-400/20 bg-sky-500/[0.04] p-4 mb-4 space-y-2.5" data-testid="access-summary">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-white"><Eye className="w-4 h-4 text-sky-300" /> What can they do?</div>
      <div className="text-[12.5px] text-slate-300" data-testid="access-summary-level">
        Role: <b className="text-white">{level}</b> ·{" "}
        {counts == null ? <span>Manage on every area{isAdmin ? ", plus Config and user management" : " (no role yet)"}</span>
          : <span>{Object.keys(eff).length} pages: {["manage", "edit", "view"].filter((k) => counts[k]).map((k) => `${counts[k]} ${NAMES[k]}`).join(" · ") || "none"}</span>}
      </div>
      {diffs.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="access-summary-pages">
          {diffs.map((id) => (
            <span key={id} className={chip((eff[id] || "none") === "none" ? "bg-rose-500/15 text-rose-200" : "bg-fuchsia-500/15 text-fuchsia-200")}>
              {label[id] || id}: {NAMES[base[id] || "none"]} → {NAMES[eff[id] || "none"]}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-x-3 gap-y-1" data-testid="access-summary-abilities">
        {caps.map((c) => (
          <span key={c.key} className={`text-[11.5px] ${has.has(c.key) ? "text-slate-200" : "text-slate-500 line-through"}`}>
            {c.label}{capOv[c.key] !== undefined && <span className="ml-1 text-fuchsia-300 no-underline">*</span>}
          </span>
        ))}
      </div>
    </div>
  );
}
